// Hộp thông báo (chuông) trên thanh đầu: số chưa đọc + danh sách lưu lâu dài, lấy từ máy chủ (không phụ thuộc kết nối WS).
// Toast chỉ là lời nhắc thoáng qua; thông báo thật nằm ở đây cho tới khi người dùng tự bấm đọc — bị bỏ lỡ toast hay tab treo đều không mất.
import { api, buildQuery, errMsg } from "./api";
import { $, busy, clear, h, toast } from "./dom";
import { store } from "./store";
import { fmtTime } from "./format";
import type { UiEvent } from "./types";

export interface Item { id: string; at: string; level: "info" | "warn" | "err"; text: string; seen: boolean; data?: unknown }
interface Inbox { items: Item[]; unread: number; nextBefore: string | null }

const PAGE = 50;
const POLL_MS = 60_000;
const LEVEL_TEXT = { info: "thông tin", warn: "cảnh báo", err: "lỗi" } as const;

let timer: number | undefined;
let panel: HTMLElement | null = null;
let listEl: HTMLElement | null = null;
let moreBtn: HTMLButtonElement | null = null;
let nextBefore: string | null = null;
let unread = 0;
let gen = 0;
let onVisible: (() => void) | null = null;
let onDocDown: ((e: Event) => void) | null = null;
let onKey: ((e: KeyboardEvent) => void) | null = null;

function setUnread(n: number): void {
  unread = Math.max(0, n);
  const badge = $("bell-count");
  badge.textContent = unread > 99 ? "99+" : String(unread);
  badge.hidden = unread === 0;
  $("bell").setAttribute("aria-label", unread ? `Thông báo, ${unread} chưa đọc` : "Thông báo, không có cái nào chưa đọc");
}

function row(it: Item): HTMLElement {
  const el = h("div", { class: `notice-item ${it.level}${it.seen ? "" : " unread"}`, "data-testid": "notice-item", dataset: { id: it.id, seen: String(it.seen) }, tabindex: "0", role: "button",
    title: it.seen ? "Đã đọc" : "Chưa đọc — bấm để đánh dấu đã đọc" },
    h("div", { class: "small muted notice-meta" }, fmtTime(it.at), " · ", h("span", { class: "tag" }, LEVEL_TEXT[it.level]), it.seen ? null : h("span", { class: "notice-dot", "aria-hidden": "true", title: "Chưa đọc" }, "●")),
    h("div", { class: "wrap notice-text", "data-testid": "notice-text" }, it.text));
  const mark = () => { if (el.dataset.seen === "false") void markSeen([it.id]); };
  el.addEventListener("click", mark);
  el.addEventListener("keydown", (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); mark(); } });
  return el;
}

function paintSeen(ids: string[] | "all"): void {
  for (const el of panel?.querySelectorAll<HTMLElement>('[data-testid="notice-item"]') ?? []) {
    if (ids === "all" || ids.includes(el.dataset.id ?? "")) {
      el.dataset.seen = "true";
      el.classList.remove("unread");
      el.title = "Đã đọc";
      el.querySelector(".notice-dot")?.remove();
    }
  }
}

const pending = new Set<string>(); // id đang chờ máy chủ xác nhận: bấm đôi không gửi hai lần
async function markSeen(ids: string[] | "all"): Promise<void> {
  const key = ids === "all" ? "*" : ids.join(",");
  if (pending.has(key)) return;
  pending.add(key);
  try {
    const r = await api<{ unread: number }>("POST", "/api/notices/seen", ids === "all" ? { all: true } : { ids });
    paintSeen(ids);
    setUnread(r.unread);
  } catch (e) { toast(`Không đánh dấu đã đọc được: ${errMsg(e)}`, "err"); }
  finally { pending.delete(key); }
}

async function loadPage(reset: boolean): Promise<void> {
  if (!panel || !listEl) return;
  const g = ++gen;
  try {
    const r = await api<Inbox>("GET", `/api/notices${buildQuery({ limit: PAGE, before: reset ? undefined : nextBefore })}`);
    if (g !== gen || !panel || !listEl) return;
    if (reset) clear(listEl);
    for (const it of r.items) listEl.append(row(it));
    nextBefore = r.nextBefore;
    if (moreBtn) moreBtn.hidden = !nextBefore;
    setUnread(r.unread);
    panel.querySelector<HTMLElement>('[data-testid="notice-empty"]')!.hidden = listEl.children.length > 0;
    panel.querySelector<HTMLElement>('[data-testid="notice-error"]')!.hidden = true;
  } catch (e) {
    if (g !== gen || !panel) return;
    const box = panel.querySelector<HTMLElement>('[data-testid="notice-error"]')!;
    box.textContent = `Không tải được thông báo: ${errMsg(e)}`;
    box.hidden = false;
  }
}

export function closeInbox(): void {
  if (!panel) return;
  panel.remove();
  panel = null; listEl = null; moreBtn = null;
  if (onDocDown) document.removeEventListener("mousedown", onDocDown, true);
  if (onKey) document.removeEventListener("keydown", onKey, true);
  onDocDown = null; onKey = null;
  $("bell").setAttribute("aria-expanded", "false");
}

export function toggleInbox(): void {
  if (panel) { closeInbox(); return; }
  const bell = $("bell");
  listEl = h("div", { class: "notice-list", "data-testid": "notice-list" });
  moreBtn = h("button", { type: "button", hidden: true, title: "Tải thêm các thông báo cũ hơn", "data-testid": "notice-more", onclick: (e: Event) => void busy(e.currentTarget as HTMLButtonElement, "Đang tải…", () => loadPage(false)) }, "Xem thêm");
  panel = h("div", { id: "notice-panel", class: "notice-panel", role: "dialog", "aria-label": "Thông báo", "data-testid": "notice-panel" },
    h("div", { class: "row between notice-head" },
      h("h3", {}, "Thông báo"),
      h("div", { class: "row" },
        h("button", { type: "button", title: "Đánh dấu tất cả thông báo của bạn là đã đọc", "data-testid": "notice-all", onclick: (e: Event) => void busy(e.currentTarget as HTMLButtonElement, "…", () => markSeen("all")) }, "Đọc hết"),
        h("button", { type: "button", title: "Đóng hộp thông báo", "data-testid": "notice-close", onclick: () => { closeInbox(); bell.focus(); } }, "Đóng"))),
    h("p", { class: "muted small", hidden: true, "data-testid": "notice-empty" }, "Chưa có thông báo nào."),
    h("p", { class: "err small", hidden: true, "data-testid": "notice-error" }),
    listEl, h("div", { class: "row center" }, moreBtn));
  document.body.append(panel);
  bell.setAttribute("aria-expanded", "true");
  onDocDown = (e) => { if (panel && !panel.contains(e.target as Node) && !bell.contains(e.target as Node)) closeInbox(); };
  onKey = (e) => { if (e.key === "Escape") { closeInbox(); bell.focus(); } };
  document.addEventListener("mousedown", onDocDown, true);
  document.addEventListener("keydown", onKey, true);
  void loadPage(true);
}


// ---------------- Cửa sổ nổi giữa màn hình ----------------
// Thông báo quan trọng (báo cáo So sánh, cảnh báo) hiện thành cửa sổ HTML ngay trước mặt, có dấu × ở góc. Đóng (×, "Đã xem", Esc) = đã đọc.
// Mở khi: có thông báo đẩy mới, vừa đăng nhập mà còn thông báo chưa đọc, quay lại tab hoặc mỗi 60 giây phát hiện cái mới (phòng gói đẩy rơi).
const POPUP_MAX = 5;
const shown = new Set<string>(); // id đã từng hiện trong cửa sổ nổi ở phiên này (không hiện lại một thông báo đã đóng dù đánh dấu đọc lỗi)
let popup: HTMLElement | null = null;
let popupItems: Item[] = [];
let popupMore = 0;
let popupKey: ((e: KeyboardEvent) => void) | null = null;

const MEDAL = ["🥇", "🥈", "🥉"];
interface ReportData { kind: "bao_cao_so_sanh"; label?: string; rows: { ownerId: string; username: string; score: number; liveDone: number }[] }
interface WarnData { kind: "canh_bao"; username: string; scope: "nhom" | "super"; reasons: string[] }

function isReport(d: unknown): d is ReportData { return !!d && typeof d === "object" && (d as { kind?: unknown }).kind === "bao_cao_so_sanh" && Array.isArray((d as { rows?: unknown }).rows); }
function isWarn(d: unknown): d is WarnData { return !!d && typeof d === "object" && (d as { kind?: unknown }).kind === "canh_bao" && Array.isArray((d as { reasons?: unknown }).reasons); }


interface AnnounceData { kind: "thong_bao"; title?: string; text?: string; images?: { id: string; src?: string }[]; from?: { username?: string } }
interface KbLoiData { kind: "kich_ban_loi"; kichBanId: string; ten: string; cau: string; cachSua: string; ma?: string }
function isKbLoi(d: unknown): d is KbLoiData {
  const x = d as Partial<KbLoiData> | null;
  return !!x && typeof x === "object" && x.kind === "kich_ban_loi" && typeof x.kichBanId === "string" && typeof x.ten === "string" && typeof x.cau === "string" && typeof x.cachSua === "string";
}
function isAnnounce(d: unknown): d is AnnounceData { return !!d && typeof d === "object" && (d as { kind?: unknown }).kind === "thong_bao"; }

/** Biến chữ thuần thành các nút chữ + liên kết bấm được (CHỈ http/https, tự bỏ dấu câu cuối). Không bao giờ dựng HTML từ chuỗi. */
export function linkify(text: string): (string | HTMLElement)[] {
  const out: (string | HTMLElement)[] = [];
  let last = 0;
  for (const m of text.matchAll(/https?:\/\/[^\s<>"']+/g)) {
    let url = m[0];
    const at = m.index ?? 0;
    const tail = url.match(/[.,;:!?)\]}]+$/)?.[0] ?? "";
    if (tail) url = url.slice(0, url.length - tail.length);
    let ok = false;
    try { const u = new URL(url); ok = u.protocol === "http:" || u.protocol === "https:"; } catch { ok = false; }
    if (!ok) continue;
    if (at > last) out.push(text.slice(last, at));
    out.push(h("a", { href: url, target: "_blank", rel: "noopener noreferrer", title: `Mở ${url} ở tab mới`, "data-testid": "np-link" }, url));
    last = at + url.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** Vẽ MỘT thông báo (báo cáo = bảng, cảnh báo = lý do, thông báo của Super = chữ + ảnh, còn lại = chữ). Dùng chung cho cửa sổ nổi và xem trước ở trang Soạn thông báo. */
export function renderNoticeCard(it: Item): HTMLElement {
  const time = fmtTime(it.at);
  if (isAnnounce(it.data)) {
    const d = it.data;
    return h("section", { class: `np-card ${it.level}`, "data-testid": "np-item", dataset: { kind: "thong_bao", id: it.id } },
      h("div", { class: "np-title" }, h("b", { "data-testid": "np-title" }, d.title || "Thông báo từ Super Boss"), h("span", { class: "small muted" }, time)),
      d.text ? h("div", { class: "np-msg", "data-testid": "np-msg" }, ...linkify(d.text)) : null,
      d.images?.length ? h("div", { class: "np-imgs", "data-testid": "np-imgs" }, ...d.images.map((im, i) => {
        const src = im.src ?? `/api/announce/images/${encodeURIComponent(im.id)}`;
        return h("a", { href: src, target: "_blank", rel: "noopener noreferrer", title: "Mở ảnh lớn ở tab mới" }, h("img", { src, alt: `Ảnh đính kèm ${i + 1}`, loading: "lazy", "data-testid": "np-img" }));
      })) : null,
      d.from?.username ? h("p", { class: "small muted" }, `Từ Super Boss ${d.from.username}`) : null);
  }  if (isReport(it.data)) {
    const me = store.user?.id;
    const rows = it.data.rows.map((r, i) => h("tr", { "data-testid": "np-row", class: r.ownerId === me ? "hl" : "", dataset: { owner: r.ownerId } },
      h("td", { class: "nowrap" }, i < 3 ? MEDAL[i]! : String(i + 1)),
      h("td", { class: "name" }, h("span", { "data-testid": "np-name" }, r.username), r.ownerId === me ? [" ", h("span", { class: "tag running", title: "Dòng của bạn" }, "bạn")] : null),
      h("td", { class: "nowrap" }, h("b", { "data-testid": "np-score" }, String(r.score)), h("div", { class: "score-bar" }, h("i", { style: `width:${Math.max(0, Math.min(100, r.score))}%` }))),
      h("td", { class: "nowrap muted small", title: "Số lần chạy thật xong trong kỳ" }, `${r.liveDone} lần`)));
    return h("section", { class: "np-card info", "data-testid": "np-item", dataset: { kind: "bao_cao", id: it.id } },
      h("div", { class: "np-title" }, h("b", {}, `Báo cáo So sánh Boss${it.data.label ? ` (${it.data.label})` : ""}`), h("span", { class: "small muted" }, time)),
      it.data.rows.length
        ? h("div", { class: "tbl" }, h("table", { "data-testid": "np-report-table" },
          h("thead", {}, h("tr", {}, h("th", { title: "Xếp hạng theo điểm tổng" }, "#"), h("th", {}, "Boss"), h("th", { title: "Điểm tổng 0–100" }, "Điểm"), h("th", { title: "Chạy thật xong" }, "Xong"))),
          h("tbody", {}, ...rows)))
        : h("p", { class: "muted small" }, "Chưa có Boss nào trong bảng."));
  }
  if (isKbLoi(it.data)) {
    // (08/10/2026) lượt CHẠY THẬT xong có lỗi (máy chủ luot-do-thi-noi.ts baoChuongLuotLoi): câu dễ hiểu + cách sửa + liên kết mở kịch bản
    const d = it.data;
    return h("section", { class: `np-card ${it.level}`, "data-testid": "np-item", dataset: { kind: "kich_ban_loi", id: it.id, ma: d.ma ?? "" } },
      h("div", { class: "np-title" }, h("b", {}, "Kịch bản chạy xong có lỗi"), h("span", { class: "small muted" }, time)),
      h("p", { class: "wrap", "data-testid": "np-kb-loi-cau" }, "Kịch bản ", h("a", { href: `#/kich-ban/${encodeURIComponent(d.kichBanId)}`, "data-testid": "np-kb-link", title: "Mở kịch bản này", onclick: () => void closePopup() }, `“${d.ten}”`), `: ${d.cau}`),
      h("p", { class: "wrap small", "data-testid": "np-kb-loi-cach-sua" }, h("b", {}, "Cách sửa: "), d.cachSua));
  }
  if (isWarn(it.data)) {
    return h("section", { class: `np-card ${it.level}`, "data-testid": "np-item", dataset: { kind: "canh_bao", id: it.id } },
      h("div", { class: "np-title" }, h("b", {}, it.data.scope === "super" ? "Lưu ý riêng cho Super" : "Cảnh báo hoạt động"), h("span", { class: "small muted" }, time)),
      h("p", {}, h("b", { "data-testid": "np-warn-name" }, it.data.username), it.data.scope === "super" ? [" ", h("span", { class: "tag", title: "Luật này chỉ báo riêng cho Super, Boss không thấy" }, "chỉ Super")] : null),
      h("ul", { "data-testid": "np-reasons" }, ...it.data.reasons.map((r) => h("li", { class: "wrap" }, r))));
  }
  return h("section", { class: `np-card ${it.level}`, "data-testid": "np-item", dataset: { kind: "chu", id: it.id } },
    h("div", { class: "np-title" }, h("b", {}, LEVEL_TEXT[it.level][0]!.toUpperCase() + LEVEL_TEXT[it.level].slice(1)), h("span", { class: "small muted" }, time)),
    h("p", { class: "wrap", "data-testid": "np-text" }, it.text));
}

function paintPopup(): void {
  if (!popup) return;
  const body = popup.querySelector<HTMLElement>('[data-testid="np-body"]')!;
  clear(body);
  for (const it of popupItems) body.append(renderNoticeCard(it));
  const note = popup.querySelector<HTMLElement>('[data-testid="np-more"]')!;
  note.hidden = popupMore <= 0;
  note.textContent = popupMore > 0 ? `Còn ${popupMore} thông báo chưa đọc khác — xem trong hộp thông báo (nút chuông) sau khi đóng cửa sổ này.` : "";
}

function openPopup(): void {
  if (popup) return;
  const x = h("button", { type: "button", class: "np-x", "aria-label": "Đóng thông báo", title: "Đóng cửa sổ này và đánh dấu các thông báo đang hiện là đã đọc", "data-testid": "notice-popup-x", onclick: () => void closePopup() }, "×");
  const ok = h("button", { type: "button", class: "pri", title: "Đóng cửa sổ này và đánh dấu các thông báo đang hiện là đã đọc", "data-testid": "notice-popup-ok", onclick: () => void closePopup() }, "Đã xem");
  popup = h("div", { class: "modal-wrap", role: "dialog", "aria-modal": "true", "aria-label": "Thông báo", "data-testid": "notice-popup" },
    h("div", { class: "modal notice-popup" }, x,
      h("h3", {}, "Thông báo"),
      h("div", { class: "np-body", "data-testid": "np-body" }),
      h("p", { class: "small muted", hidden: true, "data-testid": "np-more" }),
      h("div", { class: "row end" }, ok)));
  document.body.append(popup);
  popupKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); void closePopup(); } };
  document.addEventListener("keydown", popupKey, true);
  x.focus();
}

/** Đóng cửa sổ nổi và đánh dấu đã đọc các thông báo đang hiện (đóng lỗi mạng vẫn đóng, báo lỗi; lần tải lại sau sẽ hiện lại). */
async function closePopup(): Promise<void> {
  if (!popup) return;
  const ids = popupItems.map((i) => i.id);
  popup.remove();
  popup = null;
  if (popupKey) document.removeEventListener("keydown", popupKey, true);
  popupKey = null;
  popupItems = [];
  popupMore = 0;
  if (ids.length) await markSeen(ids);
}

/** Lấy các thông báo CHƯA ĐỌC từ máy chủ; có cái chưa từng hiện trong cửa sổ nổi thì mở (hoặc bổ sung vào) cửa sổ. */
async function presentUnread(): Promise<void> {
  const g = ++gen;
  let r: Inbox;
  try { r = await api<Inbox>("GET", `/api/notices${buildQuery({ limit: PAGE })}`); } catch { return; }
  if (g !== gen) return;
  setUnread(r.unread);
  const unreadItems = r.items.filter((i) => !i.seen);
  const fresh = unreadItems.filter((i) => !shown.has(i.id));
  if (!fresh.length) return;
  for (const i of fresh) shown.add(i.id);
  const merged = [...fresh, ...popupItems.filter((i) => !fresh.some((f) => f.id === i.id))];
  popupItems = merged.sort((a, b) => b.at.localeCompare(a.at)).slice(0, POPUP_MAX);
  popupMore = Math.max(0, r.unread - popupItems.length);
  openPopup();
  paintPopup();
}

/** Thông báo đẩy vừa tới: cập nhật số; nếu hộp đang mở thì nạp lại để thấy ngay. */
export function onNoticePush(ev: UiEvent): void {
  if (ev.type !== "notice" || !("noticeId" in ev) || !ev.noticeId) return;
  if (panel) void loadPage(true);
  void presentUnread();
}

/** Bắt đầu theo dõi khi đăng nhập: đếm ngay, đếm lại khi quay lại tab và mỗi 60 giây (phòng khi kết nối đẩy bị rơi). */
export function startInbox(): void {
  stopInbox();
  void presentUnread();
  timer = window.setInterval(() => { if (document.visibilityState === "visible") void presentUnread(); }, POLL_MS);
  onVisible = () => { if (document.visibilityState === "visible") { if (panel) void loadPage(true); void presentUnread(); } };
  document.addEventListener("visibilitychange", onVisible);
}

export function stopInbox(): void {
  closeInbox();
  if (popup) { popup.remove(); popup = null; if (popupKey) document.removeEventListener("keydown", popupKey, true); popupKey = null; popupItems = []; popupMore = 0; }
  shown.clear();
  clearInterval(timer);
  timer = undefined;
  if (onVisible) document.removeEventListener("visibilitychange", onVisible);
  onVisible = null;
  gen++;
  unread = 0;
  const badge = document.getElementById("bell-count");
  if (badge) { badge.textContent = "0"; badge.hidden = true; }
}
